// Sales Partner Dashboard Page
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Icon } from '@iconify/react';
import { usePartnerAuth } from '../../auth/PartnerAuthProvider';
import {
  collection,
  query,
  where,
  getDocs,
  orderBy,
  limit,
} from 'firebase/firestore';
import { db } from '../../lib/firebase';

export default function PartnerDashboardPage() {
  const { user, partner, loading, logout } = usePartnerAuth();
  const navigate = useNavigate();

  const [stats, setStats] = useState({
    totalSchools: 0,
    totalSubscribers: 0,
    totalEarnings: 0,
    pendingEarnings: 0,
  });
  const [referredSchools, setReferredSchools] = useState([]);
  const [commissions, setCommissions] = useState([]);
  const [loadingData, setLoadingData] = useState(true);
  const [copySuccess, setCopySuccess] = useState(false);

  // Redirect if not logged in as partner
  useEffect(() => {
    if (!loading && !user) {
      navigate('/partner/sign-in', { replace: true });
    }
  }, [user, loading, navigate]);

  // Load dashboard data
  useEffect(() => {
    if (!partner?.referral_code) return;

    async function loadData() {
      try {
        setLoadingData(true);

        // Get schools referred by this partner
        const schoolsQuery = query(
          collection(db, 'schools'),
          where('referral_code_used', '==', partner.referral_code)
        );
        const schoolsSnap = await getDocs(schoolsQuery);
        const schools = schoolsSnap.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));
        setReferredSchools(schools);

        // Get commissions
        const commissionsQuery = query(
          collection(db, 'commissions'),
          where('partner_id', '==', user.uid),
          orderBy('createdAt', 'desc'),
          limit(20)
        );
        const commissionsSnap = await getDocs(commissionsQuery);
        const commissionsList = commissionsSnap.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));
        setCommissions(commissionsList);

        // Calculate stats
        let totalSubscribers = 0;
        for (const school of schools) {
          // Count users subscribed to this school
          const usersQuery = query(
            collection(db, 'users'),
            where('school_ids', 'array-contains', school.id),
            where('subscription.status', '==', 'active')
          );
          const usersSnap = await getDocs(usersQuery);
          totalSubscribers += usersSnap.size;
        }

        setStats({
          totalSchools: schools.length,
          totalSubscribers,
          totalEarnings: partner.total_earnings || 0,
          pendingEarnings: partner.pending_earnings || 0,
        });
      } catch (err) {
        console.error('Error loading dashboard data:', err);
      } finally {
        setLoadingData(false);
      }
    }

    loadData();
  }, [partner, user]);

  const handleLogout = async () => {
    await logout();
    navigate('/partner/sign-in', { replace: true });
  };

  const copyReferralCode = () => {
    if (partner?.referral_code) {
      navigator.clipboard.writeText(partner.referral_code);
      setCopySuccess(true);
      setTimeout(() => setCopySuccess(false), 2000);
    }
  };

  const copyReferralLink = () => {
    if (partner?.referral_code) {
      const link = `https://app.ridewatch.org/sign-up?ref=${partner.referral_code}`;
      navigator.clipboard.writeText(link);
      setCopySuccess(true);
      setTimeout(() => setCopySuccess(false), 2000);
    }
  };

  if (loading || !partner) {
    return (
      <div className="min-vh-100 d-flex align-items-center justify-content-center">
        <div className="spinner-border text-success" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="min-vh-100" style={{ backgroundColor: '#f8f9fa' }}>
      {/* Header */}
      <header
        className="py-3 px-4"
        style={{
          background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
          boxShadow: '0 2px 10px rgba(0,0,0,0.1)',
        }}
      >
        <div className="container-fluid">
          <div className="d-flex justify-content-between align-items-center">
            <div className="d-flex align-items-center gap-3">
              <Icon icon="mdi:handshake" style={{ fontSize: '32px', color: 'white' }} />
              <div>
                <h5 className="mb-0 text-white fw-bold">RideWatch Partners</h5>
                <small className="text-white opacity-75">Partner Dashboard</small>
              </div>
            </div>
            <div className="d-flex align-items-center gap-3">
              <span className="text-white d-none d-md-inline">
                <Icon icon="mdi:account" className="me-1" />
                {partner.displayName}
              </span>
              <button className="btn btn-outline-light btn-sm" onClick={handleLogout}>
                <Icon icon="mdi:logout" className="me-1" />
                Logout
              </button>
            </div>
          </div>
        </div>
      </header>

      <main className="container py-4">
        {/* Referral Code Card */}
        <div className="card border-0 shadow-sm mb-4">
          <div
            className="card-body p-4"
            style={{
              background: 'linear-gradient(135deg, #f0fdf4 0%, #dcfce7 100%)',
              borderRadius: '12px',
            }}
          >
            <div className="row align-items-center">
              <div className="col-md-8">
                <h5 className="fw-bold mb-2" style={{ color: '#166534' }}>
                  <Icon icon="mdi:tag" className="me-2" />
                  Your Referral Code
                </h5>
                <div className="d-flex align-items-center gap-3 mb-3">
                  <span
                    className="fs-3 fw-bold px-4 py-2 bg-white rounded-3"
                    style={{ color: '#10b981', letterSpacing: '2px', fontFamily: 'monospace' }}
                  >
                    {partner.referral_code}
                  </span>
                  <button
                    className="btn btn-success btn-sm"
                    onClick={copyReferralCode}
                    title="Copy code"
                  >
                    <Icon icon={copySuccess ? 'mdi:check' : 'mdi:content-copy'} />
                  </button>
                </div>
                <p className="text-muted mb-0" style={{ fontSize: '14px' }}>
                  Share this code with schools. When they sign up using your code, you'll earn{' '}
                  <strong>{(partner.commission_rate * 100).toFixed(0)}% commission</strong> on every
                  subscription.
                </p>
              </div>
              <div className="col-md-4 text-md-end mt-3 mt-md-0">
                <button className="btn btn-success" onClick={copyReferralLink}>
                  <Icon icon="mdi:link" className="me-2" />
                  Copy Referral Link
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="row g-4 mb-4">
          <div className="col-md-3 col-6">
            <div className="card border-0 shadow-sm h-100">
              <div className="card-body text-center p-4">
                <div
                  className="d-inline-flex align-items-center justify-content-center rounded-circle mb-3"
                  style={{ width: '60px', height: '60px', backgroundColor: '#dbeafe' }}
                >
                  <Icon icon="mdi:school" style={{ fontSize: '28px', color: '#2563eb' }} />
                </div>
                <h3 className="fw-bold mb-1" style={{ color: '#2563eb' }}>
                  {loadingData ? '...' : stats.totalSchools}
                </h3>
                <p className="text-muted mb-0" style={{ fontSize: '14px' }}>
                  Referred Schools
                </p>
              </div>
            </div>
          </div>

          <div className="col-md-3 col-6">
            <div className="card border-0 shadow-sm h-100">
              <div className="card-body text-center p-4">
                <div
                  className="d-inline-flex align-items-center justify-content-center rounded-circle mb-3"
                  style={{ width: '60px', height: '60px', backgroundColor: '#fce7f3' }}
                >
                  <Icon icon="mdi:account-group" style={{ fontSize: '28px', color: '#db2777' }} />
                </div>
                <h3 className="fw-bold mb-1" style={{ color: '#db2777' }}>
                  {loadingData ? '...' : stats.totalSubscribers}
                </h3>
                <p className="text-muted mb-0" style={{ fontSize: '14px' }}>
                  Active Subscribers
                </p>
              </div>
            </div>
          </div>

          <div className="col-md-3 col-6">
            <div className="card border-0 shadow-sm h-100">
              <div className="card-body text-center p-4">
                <div
                  className="d-inline-flex align-items-center justify-content-center rounded-circle mb-3"
                  style={{ width: '60px', height: '60px', backgroundColor: '#d1fae5' }}
                >
                  <Icon icon="mdi:cash-multiple" style={{ fontSize: '28px', color: '#059669' }} />
                </div>
                <h3 className="fw-bold mb-1" style={{ color: '#059669' }}>
                  ${loadingData ? '...' : stats.totalEarnings.toFixed(2)}
                </h3>
                <p className="text-muted mb-0" style={{ fontSize: '14px' }}>
                  Total Earnings
                </p>
              </div>
            </div>
          </div>

          <div className="col-md-3 col-6">
            <div className="card border-0 shadow-sm h-100">
              <div className="card-body text-center p-4">
                <div
                  className="d-inline-flex align-items-center justify-content-center rounded-circle mb-3"
                  style={{ width: '60px', height: '60px', backgroundColor: '#fef3c7' }}
                >
                  <Icon icon="mdi:clock-outline" style={{ fontSize: '28px', color: '#d97706' }} />
                </div>
                <h3 className="fw-bold mb-1" style={{ color: '#d97706' }}>
                  ${loadingData ? '...' : stats.pendingEarnings.toFixed(2)}
                </h3>
                <p className="text-muted mb-0" style={{ fontSize: '14px' }}>
                  Pending Payout
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Schools and Commissions */}
        <div className="row g-4">
          {/* Referred Schools */}
          <div className="col-lg-6">
            <div className="card border-0 shadow-sm h-100">
              <div className="card-header bg-white border-0 py-3">
                <h6 className="fw-bold mb-0">
                  <Icon icon="mdi:school" className="me-2" style={{ color: '#2563eb' }} />
                  Referred Schools
                </h6>
              </div>
              <div className="card-body p-0">
                {loadingData ? (
                  <div className="text-center py-5">
                    <div className="spinner-border spinner-border-sm text-muted" />
                  </div>
                ) : referredSchools.length === 0 ? (
                  <div className="text-center py-5">
                    <Icon icon="mdi:school-outline" style={{ fontSize: '48px', color: '#d1d5db' }} />
                    <p className="text-muted mt-3 mb-0">No schools referred yet</p>
                    <small className="text-muted">Share your referral code to get started!</small>
                  </div>
                ) : (
                  <div className="list-group list-group-flush">
                    {referredSchools.map((school) => (
                      <div key={school.id} className="list-group-item px-4 py-3">
                        <div className="d-flex justify-content-between align-items-center">
                          <div>
                            <h6 className="mb-1 fw-semibold">{school.name}</h6>
                            <small className="text-muted">
                              Joined{' '}
                              {school.createdAt?.toDate
                                ? school.createdAt.toDate().toLocaleDateString()
                                : 'Recently'}
                            </small>
                          </div>
                          <span className="badge bg-success-subtle text-success">Active</span>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Recent Commissions */}
          <div className="col-lg-6">
            <div className="card border-0 shadow-sm h-100">
              <div className="card-header bg-white border-0 py-3">
                <h6 className="fw-bold mb-0">
                  <Icon icon="mdi:cash-check" className="me-2" style={{ color: '#059669' }} />
                  Recent Commissions
                </h6>
              </div>
              <div className="card-body p-0">
                {loadingData ? (
                  <div className="text-center py-5">
                    <div className="spinner-border spinner-border-sm text-muted" />
                  </div>
                ) : commissions.length === 0 ? (
                  <div className="text-center py-5">
                    <Icon icon="mdi:cash-off" style={{ fontSize: '48px', color: '#d1d5db' }} />
                    <p className="text-muted mt-3 mb-0">No commissions yet</p>
                    <small className="text-muted">Earnings appear when referred users subscribe</small>
                  </div>
                ) : (
                  <div className="list-group list-group-flush">
                    {commissions.map((commission) => (
                      <div key={commission.id} className="list-group-item px-4 py-3">
                        <div className="d-flex justify-content-between align-items-center">
                          <div>
                            <h6 className="mb-1 fw-semibold text-success">
                              +${commission.amount?.toFixed(2) || '0.00'}
                            </h6>
                            <small className="text-muted">
                              {commission.plan === 'yearly' ? 'Yearly' : 'Monthly'} subscription
                            </small>
                          </div>
                          <span
                            className={`badge ${
                              commission.status === 'paid'
                                ? 'bg-success-subtle text-success'
                                : 'bg-warning-subtle text-warning'
                            }`}
                          >
                            {commission.status === 'paid' ? 'Paid' : 'Pending'}
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Help Section */}
        <div className="card border-0 shadow-sm mt-4">
          <div className="card-body p-4">
            <h6 className="fw-bold mb-3">
              <Icon icon="mdi:help-circle" className="me-2" style={{ color: '#6b7280' }} />
              How It Works
            </h6>
            <div className="row g-4">
              <div className="col-md-4">
                <div className="d-flex gap-3">
                  <div
                    className="rounded-circle d-flex align-items-center justify-content-center flex-shrink-0"
                    style={{ width: '40px', height: '40px', backgroundColor: '#dbeafe' }}
                  >
                    <span className="fw-bold" style={{ color: '#2563eb' }}>
                      1
                    </span>
                  </div>
                  <div>
                    <h6 className="fw-semibold mb-1">Share Your Code</h6>
                    <p className="text-muted mb-0" style={{ fontSize: '14px' }}>
                      Share your unique referral code with schools and bus companies
                    </p>
                  </div>
                </div>
              </div>
              <div className="col-md-4">
                <div className="d-flex gap-3">
                  <div
                    className="rounded-circle d-flex align-items-center justify-content-center flex-shrink-0"
                    style={{ width: '40px', height: '40px', backgroundColor: '#fce7f3' }}
                  >
                    <span className="fw-bold" style={{ color: '#db2777' }}>
                      2
                    </span>
                  </div>
                  <div>
                    <h6 className="fw-semibold mb-1">They Sign Up</h6>
                    <p className="text-muted mb-0" style={{ fontSize: '14px' }}>
                      Schools register with your code and start using RideWatch
                    </p>
                  </div>
                </div>
              </div>
              <div className="col-md-4">
                <div className="d-flex gap-3">
                  <div
                    className="rounded-circle d-flex align-items-center justify-content-center flex-shrink-0"
                    style={{ width: '40px', height: '40px', backgroundColor: '#d1fae5' }}
                  >
                    <span className="fw-bold" style={{ color: '#059669' }}>
                      3
                    </span>
                  </div>
                  <div>
                    <h6 className="fw-semibold mb-1">Earn Commission</h6>
                    <p className="text-muted mb-0" style={{ fontSize: '14px' }}>
                      Get {(partner.commission_rate * 100).toFixed(0)}% of every subscription payment, forever
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}
